<template>
  <CardBase class="cursor-pointer" :class="isCollection ? 'group' : ''" @click="goToResource(resource)">
    <NuxtLink v-if="resource" :to="goToResource(resource)" :external="resource.type?.toLowerCase() === 'article'">
      <div class="relative">
        <div class="relative h-full w-full">
          <div
            class="relative z-10 -mx-0.5 -mt-0.5 h-52 overflow-hidden border-2 border-transparent group-hover:border-shade-900 md:h-60"
            :class="isCollection
                ? 'transition-all duration-200 ease-out group-hover:-translate-x-2 group-hover:-translate-y-2 transform-gpu'
                : ''
              ">
            <div v-if="resource.image" class="absolute -inset-0.5 border-2 border-shade-900 bg-white">
              <img v-if="resource.image.isIcon" src="~/assets/images/orange_square.png" :alt="resource.type"
                class="center bg-white p-4" />
              <img v-else class="h-full w-full" :class="resource.image?.centered
                  ? 'object-scale-down p-4'
                  : 'object-cover'
                " :src="resource.image?.url" :alt="resource.image?.alt" />
            </div>
            <div v-if="resource.image && resource.image?.centered" class="absolute -inset-12 -z-10 bg-shade-300">
              <div class="inset absolute z-10 h-full w-full bg-shade-alpha-900"></div>
              <BasicIcon v-if="resource.image.isIcon" :type="resource.type" class="h-5/6 bg-white p-4" />
              <img v-else class="-z-10 h-full w-full object-fill blur-3xl" :src="resource.image?.url"
                :alt="resource.image?.alt" />
            </div>
          </div>
          <div
            class="absolute -inset-x-px inset-y-0 border-b-2 border-shade-900 transition-opacity duration-300 ease-out">
          </div>
          <div v-if="isCollection"
            class="absolute bottom-1 left-0 right-0.5 top-0 border-b-2 border-r-2 border-shade-900 opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100">
          </div>
        </div>
        <div class="absolute bottom-4 left-4 z-10 mx-4 flex items-center bg-white p-1 text-sm">
          <BasicIcon :type="(resource.type as ResourceType)" class="h-6 w-6" />
          <span v-if="te(`resource.type.${resource.type?.toLowerCase()}`)" class="mx-2">
            {{ $t(`resource.type.${resource.type?.toLowerCase()}`) }}
          </span>
          <span v-else class="mx-2 first-letter:uppercase">{{
            resource.type
            }}</span>
        </div>
      </div>
      <section class="flex h-40 flex-col items-center gap-1 p-4">
        <!-- Title -->
        <h4 v-if="locale === 'fr'" class="text-xs text-center font-black first-letter:uppercase"
          :class="resource.type === 'work' ? 'italic' : ''" v-html="resource.title" />
        <h4 v-else-if="locale === 'en' && !resource.titleEn"
          class="text--xs text-center font-black first-letter:uppercase"
          :class="resource.type === 'work' ? 'italic' : ''" v-html="resource.title" />
        <h4 v-else
          class="text-xs text-center font-black transition-colors duration-200 ease-in-out group-hover:text-primary-400"
          :class="resource.type === 'work' ? 'italic' : ''" v-html="resource.titleEn" />
        <!-- Description -->
        <div v-if="resource.type !== 'book'" class="font-sans">
          <p v-if="locale === 'en' && resource.cardDescriptionEn" class="text-ellipsis text-sm"
            v-html="resource.cardDescriptionEn.slice(0, 100)" />
          <p v-else-if="resource.cardDescription" :class="'text-sm text-ellipsis'"
            v-html="resource.cardDescription.slice(0, 100)" />
        </div>
        <!-- Persons -->
        <div class="flex">
          <span v-if="byPersons || resource.type === 'workcourse'" class="first-letter:uppercase">{{ $t('labels.by')
            }}&nbsp;</span>
          <template v-for="(person, index) in resource.cardPersons?.slice(
            0,
            resource.type === 'livre' ? 1 : 3
          )">
            <p :class="byPersons ? '' : 'cursor-pointer text-primary-400'"
              class="transition-colors duration-200 ease-in-out hover:text-primary-100">
              {{ person.firstName }} {{ person.lastName
              }}<span v-if="
                index !== resource.cardPersons.length - 1 &&
                resource.type !== 'livre'
              ">,&nbsp;</span>
            </p>
          </template>
        </div>

        <!-- Date -->
        <div v-if="resource.cardDate" class="font-sans text-sm text-shade-500">
          <span v-if="['audio', 'video', 'collection'].includes(resource.type)">{{ formatToFullDate(resource.cardDate)
            }}</span>

          <span v-else>{{ parseISO(resource.cardDate).getFullYear() }}</span>
        </div>

        <div class="flex flex-wrap">
          <DataTag v-for="tag in resource.tags" :key="tag.label" :tag="tag" />
        </div>
      </section>
    </NuxtLink>
  </CardBase>
</template>
<script lang="ts" setup>
import { PropType } from 'vue'
import { useI18n } from '#i18n'
const { formatToFullDate } = useFullDateFormater()
import { parseISO } from 'date-fns'

const props = defineProps({
  resource: {
    type: Object as PropType<ResourceCard>,
    required: true,
  },
  byPersons: {
    type: Boolean,
    default: false,
  },
})

const { te, locale } = useI18n()
const isCollection = computed<boolean>(() => {
  return props.resource.type === 'Collection'
})
const { goToResource } = useGoToResourceLink()
</script>
